import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { Vehicle } from '../../types/vehicle';
import vehicleService from '../../services/vehicleService';
import { userService } from '../../services/userService';

interface VehicleAssignmentProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onAssign: () => void;
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
}

const VehicleAssignment: React.FC<VehicleAssignmentProps> = ({
  vehicle,
  open,
  onClose,
  onAssign,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const organizationUsers = await userService.getOrganizationUsers(vehicle.organizationId);
        setUsers(organizationUsers);
      } catch (err) {
        console.error('Error loading users:', err);
        setError(t('vehicle.assignment.errors.loadFailed'));
      }
    };

    loadUsers();
  }, [vehicle.organizationId]);

  const handleUserChange = (event: SelectChangeEvent) => {
    setSelectedUserId(event.target.value);
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      setError(t('vehicle.assignment.errors.userRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await vehicleService.assignVehicle(vehicle.id, selectedUserId);
      onAssign();
      onClose();
    } catch (err) {
      console.error('Error assigning vehicle:', err);
      setError(err instanceof Error ? err.message : t('vehicle.assignment.errors.assignFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    setLoading(true);
    setError(null);

    try {
      await vehicleService.unassignVehicle(vehicle.id);
      onAssign();
      onClose();
    } catch (err) {
      console.error('Error unassigning vehicle:', err);
      setError(err instanceof Error ? err.message : t('vehicle.assignment.errors.unassignFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {vehicle.currentAssignment ? t('vehicle.assignment.reassignTitle') : t('vehicle.assignment.assignTitle')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ typography: 'body1', color: 'error.main', mb: 2 }}>
            {error}
          </Box>
        )}

        {vehicle.currentAssignment && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ typography: 'subtitle2', color: 'text.secondary' }}>
              {t('vehicle.assignment.currentlyAssigned')}:
            </Box>
            {users.length > 0 ? (
              <Box sx={{ typography: 'body1' }}>
                {users.find(u => u.id === vehicle.currentAssignment?.userId)?.firstName}{' '}
                {users.find(u => u.id === vehicle.currentAssignment?.userId)?.lastName}
              </Box>
            ) : (
              <Box sx={{ typography: 'body1', color: 'text.secondary' }}>
                {t('vehicle.assignment.loadingUser')}
              </Box>
            )}
          </Box>
        )}

        {users.length > 0 ? (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('vehicle.assignment.selectUser')}</InputLabel>
            <Select
              value={selectedUserId}
              onChange={handleUserChange}
              label="Assign to User"
            >
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ typography: 'body1', color: 'text.secondary', mb: 2 }}>
            {t('vehicle.assignment.loadingUsers')}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading || users.length === 0}>
          {t('common.cancel')}
        </Button>
        {vehicle.currentAssignment && (
          <Button
            onClick={handleUnassign}
            color="secondary"
            disabled={loading || users.length === 0}
          >
            {t('vehicle.assignment.unassign')}
          </Button>
        )}
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={!selectedUserId || loading || users.length === 0}
        >
          {vehicle.currentAssignment ? t('vehicle.assignment.reassign') : t('vehicle.assignment.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleAssignment;
